import { proxy, useSnapshot } from "valtio";
import { APIGetUserResBody, APIGetAppSettingsResBody, UserRole, ClientPricingPlan } from "@spona/rest-api-types";

const PLAN_HIERARCHY: Record<string, number> = {
    [ClientPricingPlan.STARTER]: 0,
    [ClientPricingPlan.STANDARD]: 1,
    [ClientPricingPlan.PRO]: 2,
    [ClientPricingPlan.CUSTOM]: 3,
};

// TODO: Update GlobalAppState with proper types
export interface GlobalAppState {
    settings: APIGetAppSettingsResBody | undefined;
    user: APIGetUserResBody | undefined;
    isMobileMode: boolean;
    isInitializing: boolean;  // True while fetching user/settings
    isInitialized: boolean;   // True after first fetch attempt (success or fail)
    initError: string | null; // Error message if initialization failed
};

// TODO: Add types
const appState = proxy<GlobalAppState>({
    settings: undefined,
    user: undefined,
    isMobileMode: false, // Default to desktop mode
    isInitializing: false,
    isInitialized: false,
    initError: null
});

// Export the proxy for direct access in non-React contexts (like services)
export const appStateProxy = appState;

const useAppState = () => {

    const appStateSnapshot = useSnapshot(appState);

    const isAdmin = () => {
        if (appStateSnapshot.user) {
            return appStateSnapshot.user.role === 'admin';
        } else {
            return false;
        }
    };

    const isProductionWorker = () => {
        return appStateSnapshot.user?.role === UserRole.PRODUCTION_WORKER;
    };
    
    const canPrint = () => {
        const role = appStateSnapshot.user?.role;
        return role !== UserRole.EXTERNAL_SELLER && role !== UserRole.PRODUCTION_WORKER;
    };
    
    return {
        isAdmin,
        isProductionWorker,
        canPrint,
        userHasMultipleGroups: () => appStateSnapshot.user ? appStateSnapshot.user.groups.length > 1 : false,

        // Getters
        getSettings: () => appStateSnapshot.settings,
        getUser: () => appStateSnapshot.user,
        getUserRole: () => appStateSnapshot.user?.role ?? undefined,
        getUserGroups: () => appStateSnapshot.user?.groups ?? [],
        getClientUsername: () => {
            const client = appStateSnapshot.user?.client;
            // clientId can be either a string (ObjectId) or a populated Client object
            // Access username only if client is populated (object, not string)
            return typeof client === 'object' && client ? client.username : undefined;
        },
        getClientPlan: () => {
            const client = appStateSnapshot.user?.client;
            return typeof client === 'object' && client ? (client as any).plan as ClientPricingPlan | undefined : undefined;
        },
        isPlanAtLeast: (minPlan: ClientPricingPlan) => {
            const client = appStateSnapshot.user?.client;
            const plan = typeof client === 'object' && client ? (client as any).plan as string | undefined : undefined;
            if (!plan) return false;
            return (PLAN_HIERARCHY[plan] ?? -1) >= (PLAN_HIERARCHY[minPlan] ?? 0);
        },
        getClientLogoUrl: () => {
            const client = appStateSnapshot.user?.client;
            return typeof client === 'object' && client ? (client as any).logoUrl : undefined;
        },
        isMobileMode: () => appStateSnapshot.isMobileMode,
        
        // Initialization state getters
        isInitializing: () => appStateSnapshot.isInitializing,
        isInitialized: () => appStateSnapshot.isInitialized,
        getInitError: () => appStateSnapshot.initError,

        // Setters
        setUser: (user: APIGetUserResBody | undefined) => appState.user = user,
        setSettings: (settings: APIGetAppSettingsResBody | undefined) => appState.settings = settings,
        setMobileMode: (enabled: boolean) => appState.isMobileMode = enabled,
        
        // Initialization control
        setInitializing: (value: boolean) => appState.isInitializing = value,
        setInitialized: (value: boolean) => appState.isInitialized = value,
        setInitError: (error: string | null) => appState.initError = error,
        
        // Reset state (for logout)
        reset: () => {
            appState.user = undefined;
            appState.settings = undefined;
            appState.isInitialized = false;
            appState.isInitializing = false;
            appState.initError = null;
        }
    }
};

export default useAppState;